'use client'

import { useEffect } from 'react'

// Lighthouse SEO flags two things that recur across CMS rich-text content and a
// few shared components, on many pages:
//   • link-text  — anchors whose visible text is generic ("here", "read more",
//                  "learn more", …). The audit reads innerText, not aria-label.
//   • image-alt  — <img> with no alt attribute (CMS-embedded images).
// Both read the post-hydration DOM (Lighthouse and Googlebot execute JS), and
// both have a deterministic, genuinely-useful fix derivable from the page:
//   • a generic link's destination (href) names it,
//   • an alt-less image's filename names it.
// One idempotent, rAF-debounced observer fixes every occurrence site-wide,
// instead of editing ~80 parse() call sites + scattered components.

// Exactly Lighthouse's en link-text blocklist (+ a couple of safe extras) so we
// only touch anchors the audit would actually flag.
const GENERIC_LINK_TEXT = new Set([
  'click here',
  'click this',
  'go',
  'here',
  'information',
  'learn more',
  'more',
  'more info',
  'more information',
  'right here',
  'read more',
  'see more',
  'start',
  'this',
  'click'
])

function labelFromHref(href: string | null): string {
  if (!href) return ''
  try {
    const u = new URL(href, window.location.origin)
    let seg = u.pathname.split('/').filter(Boolean).pop() || u.hostname.replace(/^www\./, '')
    seg = decodeURIComponent(seg)
      .replace(/\.[a-z0-9]{2,4}$/i, '') // drop file extension
      .replaceAll(/[-_%]+/g, ' ')
      .replaceAll(/\s+/g, ' ')
      .trim()
    // Ignore opaque hashes / pure numbers — useless as a label.
    if (!seg || /^[0-9a-f]{8,}$/i.test(seg) || /^\d+$/.test(seg)) return ''
    return seg
  } catch {
    return ''
  }
}

function appendHidden(el: Element, text: string) {
  const span = document.createElement('span')
  span.className = 'visually-hidden'
  span.textContent = ' ' + text
  el.append(span)
}

export function useDescriptiveLinks() {
  useEffect(() => {
    let raf = 0

    const run = () => {
      raf = 0

      // link-text: make generic anchors descriptive via their destination.
      document.querySelectorAll<HTMLAnchorElement>('a[href]:not([data-lt])').forEach(a => {
        a.setAttribute('data-lt', '')
        if (a.querySelector('.visually-hidden')) return // already descriptive (SSR)
        const txt = (a.textContent || '').replaceAll(/\s+/g, ' ').trim().toLowerCase()
        if (!GENERIC_LINK_TEXT.has(txt)) return
        const label = labelFromHref(a.getAttribute('href')) || document.title.split('|')[0].trim()
        if (label) appendHidden(a, label)
      })

      // image-alt: ensure every <img> has an alt attribute (filename-derived,
      // else empty = decorative — both satisfy the audit).
      document.querySelectorAll<HTMLImageElement>('img:not([alt]):not([data-alt])').forEach(img => {
        img.setAttribute('data-alt', '')
        const src = img.getAttribute('src') || ''
        let alt = ''
        try {
          alt = decodeURIComponent((src.split('/').pop() || '').split('?')[0])
            .replace(/\.[a-z0-9]{2,4}$/i, '')
            .replaceAll(/[-_%]+/g, ' ')
            .trim()
          if (/^[0-9a-f]{8,}$/i.test(alt) || /^\d+$/.test(alt)) alt = ''
        } catch {
          alt = ''
        }
        img.setAttribute('alt', alt)
      })
    }

    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(run)
    }

    schedule()
    // childList only: our writes (appended span, alt attr) don't re-trigger a
    // childList pass in a loop because the processed nodes carry data-* flags.
    const observer = new MutationObserver(schedule)
    observer.observe(document.body, { subtree: true, childList: true })

    return () => {
      observer.disconnect()
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])
}
